import React, { useEffect, useRef, useState } from 'react'
import { Check, Download, Package, Pencil, Trash2, Upload, Wrench, X } from 'lucide-react'
import styles from './PackagesTab.module.css'
import { toolPackagesApi } from '../../../lib/apiClient'
import { toast } from '../../../lib/toast'
import Button from '../../../ui/Button/Button'
import InputModal from '../../../ui/inputModal/InputModal'
import ToggleSwitch from '../../../ui/ToggleSwitch/ToggleSwitch'

interface ToolPackage {
  id: string
  name: string
  description: string
  version: string
  connectionTypeId: number
  toolCount: number
  enabled: boolean
}

// Max file size: 2 MB
const MAX_FILE_SIZE = 2 * 1024 * 1024

interface PackagesTabProps {
  showUpload: boolean
  setShowUpload: (show: boolean) => void
}

const PackagesTab: React.FC<PackagesTabProps> = ({ showUpload, setShowUpload }) => {
  const [packages, setPackages] = useState<ToolPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [packageToDelete, setPackageToDelete] = useState<ToolPackage | null>(null)
  const [deletingPackageId, setDeletingPackageId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [savingNameId, setSavingNameId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadPackages()
  }, [])

  useEffect(() => {
    if (showUpload) {
      fileInputRef.current?.click()
      setShowUpload(false)
    }
  }, [showUpload])

  const loadPackages = async () => {
    try {
      const data = await toolPackagesApi.list()
      setPackages(Array.isArray(data) ? data : [])
    } catch (error: any) {
      toast.error(`Failed to load packages: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async (pkg: ToolPackage) => {
    try {
      const data = await toolPackagesApi.get(pkg.id)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const safeName = pkg.name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase()
      a.download = `${safeName}-v${pkg.version}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error: any) {
      toast.error(`Failed to export package: ${error.message}`)
    }
  }

  const handleStartEdit = (pkg: ToolPackage) => {
    setEditingId(pkg.id)
    setEditingName(pkg.name)
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingName('')
  }

  const handleSaveName = async (pkg: ToolPackage) => {
    const trimmed = editingName.trim()
    if (!trimmed || trimmed === pkg.name) {
      handleCancelEdit()
      return
    }
    setSavingNameId(pkg.id)
    try {
      await toolPackagesApi.updateName(pkg.id, trimmed)
      setPackages(prev => prev.map(p => p.id === pkg.id ? { ...p, name: trimmed } : p))
      toast.success('Package renamed')
    } catch (error: any) {
      toast.error(`Failed to rename package: ${error.message}`)
    } finally {
      setSavingNameId(null)
      setEditingId(null)
      setEditingName('')
    }
  }

  const handleDelete = (pkg: ToolPackage) => {
    setPackageToDelete(pkg)
  }

  const handleConfirmDelete = async () => {
    if (!packageToDelete || deletingPackageId === packageToDelete.id) return
    const target = packageToDelete
    try {
      setDeletingPackageId(target.id)
      await toolPackagesApi.delete(target.id)
      setPackages(prev => prev.filter(p => p.id !== target.id))
      toast.success('Package deleted')
    } catch (error: any) {
      toast.error(`Failed to delete package: ${error.message}`)
    } finally {
      setDeletingPackageId(null)
      setPackageToDelete(null)
    }
  }

  const handleToggleEnabled = async (pkg: ToolPackage) => {
    const newEnabled = !pkg.enabled
    setTogglingId(pkg.id)
    try {
      await toolPackagesApi.toggleEnabled(pkg.id, newEnabled)
      setPackages(prev => prev.map(p => p.id === pkg.id ? { ...p, enabled: newEnabled } : p))
      toast.success(`Package ${newEnabled ? 'enabled' : 'disabled'}`)
    } catch (error: any) {
      toast.error(`Failed to update package: ${error.message}`)
    } finally {
      setTogglingId(null)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.json')) {
      toast.error('Only .json files are allowed')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large. Maximum size is 2 MB.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    try {
      const text = await file.text()

      if (text.includes('<script') || text.includes('javascript:') || text.includes('data:text/html')) {
        toast.error('File contains potentially malicious content')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      let json: any
      try {
        json = JSON.parse(text)
      } catch {
        toast.error('Invalid JSON: failed to parse file')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      if (!json.package?.name || !json.package?.connectionTypeId || !Array.isArray(json.tools) || json.tools.length === 0) {
        toast.error('Invalid package: must contain "package" metadata (with name and connectionTypeId) and a non-empty "tools" array')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      setUploading(true)
      await toolPackagesApi.create({
        name: json.package.name,
        description: json.package.description || '',
        version: json.package.version || '1.0.0',
        connectionTypeId: json.package.connectionTypeId,
        tools: json.tools,
      })
      toast.success(`Package "${json.package.name}" uploaded successfully`)
      await loadPackages()
    } catch (error: any) {
      toast.error(`Upload failed: ${error.message}`)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  if (loading) {
    return <div className={styles.loading}>Loading packages...</div>
  }

  return (
    <div className={styles.packagesTab}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className={styles.fileInput}
        onChange={handleFileSelect}
      />

      {packages.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.logo}>
            <Package size={48} />
          </div>
          <h3>No packages yet</h3>
          <p>Upload a tool package JSON file to make it available for installation</p>
          <Button
            variant="outline"
            leftIcon={<Upload size={16} />}
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}
          >
            Upload Package
          </Button>
        </div>
      ) : (
        <div className={styles.packagesList}>
          {packages.map(pkg => (
            <div key={pkg.id} className={styles.packageCard}>
              <div className={styles.packageInfo}>
                <div className={styles.packageIcon}>
                  <Package size={20} />
                </div>
                <div className={styles.packageDetails}>
                  {editingId === pkg.id ? (
                    <div className={styles.nameEdit}>
                      <input
                        className={styles.nameInput}
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveName(pkg)
                          if (e.key === 'Escape') handleCancelEdit()
                        }}
                        autoFocus
                        disabled={savingNameId === pkg.id}
                        maxLength={255}
                      />
                      <button
                        className={styles.nameEditBtn}
                        onClick={() => handleSaveName(pkg)}
                        disabled={savingNameId === pkg.id}
                        title="Save name"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        className={styles.nameEditCancelBtn}
                        onClick={handleCancelEdit}
                        disabled={savingNameId === pkg.id}
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className={styles.nameRow}>
                      <h3>{pkg.name}</h3>
                      <button
                        className={styles.renameBtn}
                        onClick={() => handleStartEdit(pkg)}
                        title="Rename package"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  )}
                  {pkg.description && <p className={styles.packageDesc}>{pkg.description}</p>}
                  <div className={styles.packageMeta}>
                    <span className={styles.metaItem}>
                      <Wrench size={12} />
                      {pkg.toolCount} tool{pkg.toolCount !== 1 ? 's' : ''}
                    </span>
                    <span className={styles.metaItem}>
                      v{pkg.version}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.packageActions}>
                <ToggleSwitch
                  checked={pkg.enabled}
                  onChange={() => handleToggleEnabled(pkg)}
                  disabled={togglingId === pkg.id}
                />
                <button
                  className={styles.exportBtn}
                  onClick={() => handleExport(pkg)}
                  title="Download package JSON"
                >
                  <Download size={16} />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(pkg)}
                  title="Delete package"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <InputModal
        open={packageToDelete !== null}
        title="Delete Package"
        showInput={false}
        danger
        message={`Delete package "${packageToDelete?.name}"? This will not remove tools already imported from this package.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={packageToDelete !== null && deletingPackageId === packageToDelete.id}
        loadingText="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deletingPackageId) {
            setPackageToDelete(null)
          }
        }}
      />
    </div>
  )
}

export default PackagesTab
